<template>
  <!-- Esqueleto de carregamento no formato da tela que vai aparecer, no lugar
       do spinner: a pessoa ja ve onde cada coisa fica. Um componente para o
       portal inteiro, com uma variante por formato de pagina. -->
  <div
    class="psk"
    :class="`psk--${variant}`"
    role="status"
    aria-live="polite"
    :aria-label="$t('common.loading')"
  >
    <!-- painel: titulo, faixa de indicadores e dois blocos -->
    <template v-if="variant === 'dashboard'">
      <v-skeleton-loader type="heading" class="psk__heading" />
      <div class="psk__stats">
        <v-skeleton-loader
          v-for="n in 3"
          :key="n"
          type="image"
          height="112"
          class="psk__card"
        />
      </div>
      <div class="psk__split">
        <div class="psk__panel">
          <v-skeleton-loader type="article" />
        </div>
        <div class="psk__panel">
          <v-skeleton-loader type="list-item-avatar-two-line@3" />
        </div>
      </div>
    </template>

    <!-- grade de cards (turmas, cursos, modulos, treinamentos) -->
    <template v-else-if="variant === 'cards'">
      <v-skeleton-loader type="heading" class="psk__heading" />
      <div class="psk__grid">
        <v-skeleton-loader
          v-for="n in count"
          :key="n"
          type="image, list-item-two-line"
          class="psk__card psk__panel"
        />
      </div>
    </template>

    <!-- lista (notificacoes, alunos, convites) -->
    <template v-else-if="variant === 'list'">
      <v-skeleton-loader v-if="!bare" type="heading" class="psk__heading" />
      <div :class="{ psk__panel: !bare }">
        <v-skeleton-loader :type="`list-item-avatar-two-line@${count}`" />
      </div>
    </template>

    <!-- linhas compactas, dentro de abas e dialogos -->
    <template v-else-if="variant === 'rows'">
      <v-skeleton-loader :type="`list-item-two-line@${count}`" />
    </template>

    <!-- conteudo de uma aula / pagina de detalhe -->
    <template v-else-if="variant === 'detail'">
      <v-skeleton-loader type="heading" class="psk__heading" />
      <div class="psk__panel">
        <v-skeleton-loader type="image" height="220" />
        <v-skeleton-loader type="article, paragraph" />
      </div>
    </template>

    <!-- agenda: barra do mes e a grade dos dias -->
    <template v-else-if="variant === 'calendar'">
      <v-skeleton-loader type="heading" class="psk__heading" />
      <div class="psk__panel">
        <v-skeleton-loader type="text" width="40%" class="mb-4" />
        <div class="psk__month">
          <v-skeleton-loader
            v-for="n in 35"
            :key="n"
            type="image"
            height="72"
            class="psk__day"
          />
        </div>
      </div>
    </template>

    <!-- perfil: cabecalho com foto, abas e campos -->
    <template v-else-if="variant === 'profile'">
      <div class="psk__panel">
        <v-skeleton-loader type="list-item-avatar-three-line" />
      </div>
      <div class="psk__tabs">
        <v-skeleton-loader v-for="n in 4" :key="n" type="chip" />
      </div>
      <div class="psk__panel">
        <v-skeleton-loader type="text@2, sentences, text@3" class="psk__form" />
      </div>
    </template>

    <!-- financeiro: o valor a pagar em destaque e o extrato -->
    <template v-else-if="variant === 'finance'">
      <div class="psk__split psk__split--hero">
        <div class="psk__panel">
          <v-skeleton-loader type="text, heading, sentences" />
        </div>
        <v-skeleton-loader type="image" height="150" class="psk__card" />
      </div>
      <div class="psk__panel mt-5">
        <v-skeleton-loader type="text" width="30%" class="mb-3" />
        <v-skeleton-loader :type="`list-item-two-line@${count}`" />
      </div>
    </template>

    <!-- conversa: baloes alternados -->
    <template v-else-if="variant === 'chat'">
      <div class="psk__chat">
        <v-skeleton-loader
          v-for="n in count"
          :key="n"
          type="text@2"
          class="psk__bubble"
          :class="{ 'psk__bubble--mine': n % 2 === 0 }"
        />
      </div>
    </template>

    <!-- folha de exercicio -->
    <template v-else-if="variant === 'sheet'">
      <v-skeleton-loader type="text" width="20%" class="mb-3" />
      <v-skeleton-loader type="heading" class="psk__heading" />
      <div class="psk__panel">
        <v-skeleton-loader
          v-for="n in count"
          :key="n"
          type="sentences"
          class="psk__line"
        />
      </div>
    </template>
  </div>
</template>

<script>
const DEFAULT_COUNT = {
  cards: 6,
  list: 5,
  rows: 3,
  finance: 4,
  chat: 6,
  sheet: 5,
};

export default {
  name: "PageSkeleton",

  props: {
    // dashboard | cards | list | rows | detail | calendar | profile | finance
    // | chat | sheet
    variant: { type: String, default: "list" },
    // Quantidade de cards/linhas/baloes; cada variante tem um padrao.
    rows: { type: Number, default: null },
    // list sem titulo e sem moldura (quando a pagina ja mostra o titulo).
    bare: { type: Boolean, default: false },
  },

  computed: {
    count() {
      return this.rows || DEFAULT_COUNT[this.variant] || 4;
    },
  },
};
</script>

<style scoped>
.psk {
  width: 100%;
}

.psk__heading {
  max-width: 360px;
  margin-bottom: 18px;
}

.psk__panel {
  padding: 16px;
  border-radius: 18px;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.07);
}

.psk__card ::v-deep .v-skeleton-loader__image {
  border-radius: 18px;
}

.psk__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.psk__split {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 16px;
}

.psk__split--hero {
  grid-template-columns: minmax(0, 1fr) minmax(220px, 0.45fr);
}

.psk__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.psk__grid .psk__panel {
  padding: 0;
  overflow: hidden;
}

.psk__month {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}

.psk__day ::v-deep .v-skeleton-loader__image {
  border-radius: 10px;
}

.psk__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0;
}

.psk__form ::v-deep .v-skeleton-loader__text {
  margin-bottom: 18px;
}

.psk__chat {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
}

.psk__bubble {
  width: min(70%, 420px);
  padding: 12px 14px;
  border-radius: 16px 16px 16px 4px;
  background: var(--dk-surface, #fff);
}

.psk__bubble--mine {
  align-self: flex-end;
  border-radius: 16px 16px 4px 16px;
}

.psk__line + .psk__line {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px dashed rgba(var(--dk-ink-rgb, 35, 26, 46), 0.1);
}

@media (max-width: 760px) {
  .psk__split,
  .psk__split--hero {
    grid-template-columns: 1fr;
  }
  .psk__month {
    gap: 4px;
  }
  .psk__day ::v-deep .v-skeleton-loader__image {
    height: 44px !important;
  }
}

/* Quem pede menos movimento ve os blocos parados. */
@media (prefers-reduced-motion: reduce) {
  .psk ::v-deep .v-skeleton-loader__bone::after {
    animation: none;
  }
}
</style>
